/* PackageExplorerLmrbRenderer.js */

"use strict";
/* jshint -W078 */

const _ = require("lodash");
const Steve = require("@gluenetworks/steve_core").getInstance();
const AvailablePackageList = require("./availablepackagelist/AvailablePackagesListComponent");
const InputSearchControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputSearch;
const ButtonGroupControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single.ButtonGroup;
const ImportCapsuleComponent = require("./import/ImportCapsuleComponent");

class PackageExplorerLmrbRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("package-explorer-lmrb-gl");
        this._rootElem.component = this._model;
        this._rootElem.classList.add("absolute");

        //DOM Structure
        this._headerElem = undefined;
        this._footerElem = undefined;
        this._installedAvailableBtn = undefined;
        this._filterStringControl = undefined;
        this._packageExplorerContentElem = undefined;
        this._availablePackagesList = undefined;
        this._renderProm = undefined;

        //Setup the Repo List Widget
        this._availablePackagesList = new AvailablePackageList();

        this.render();
        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._rootElem.remove();
            this._rootElem = undefined;
            this._headerElem = undefined;
            this._installedAvailableBtn = undefined;
            this._filterStringControl = undefined;
            this._packageExplorerContentElem = undefined;
            this._availablePackagesList = undefined;
            this._importActionBtn = undefined;
            this._renderProm = undefined;

            this._model = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
        this._model.on("AvailablePackagesReplaced", (this._onAvailablePackagesReplaced = this._onAvailablePackagesReplaced.bind(this)));
        this._model.on("FilterStringChanged", (this._onFilterStringChanged = this._onFilterStringChanged.bind(this)));
        this._model.on("FilterInstalledOnlyChanged", (this._onFilterInstalledOnlyChanged = this._onFilterInstalledOnlyChanged.bind(this)));
        this._model.on("ExpandAll", (this._onExpandAll = this._onExpandAll.bind(this)));
        this._model.on("CollapseAll", (this._onCollapseAll = this._onCollapseAll.bind(this)));
        this._model.on("PackageUpdated", (this._onPackageUpdated = this._onPackageUpdated.bind(this)));
        this._availablePackagesList.on("LaunchEditor", (this._onLaunchEditor = this._onLaunchEditor.bind(this)));
        this._importActionBtn.addEventListener("click", (this._onImport = this._onImport.bind(this)));

        Steve.router.on("RouteDetailsChanged", (this._onRouteInfoChanged = _.throttle(this._onRouteInfoChanged.bind(this), 500)));
    }
    _unregisterListeners() {
        this._model.removeListener("Shutdown", this._onShutdown);
        this._model.removeListener("AvailablePackagesReplaced", this._onAvailablePackagesReplaced);
        this._model.removeListener("FilterStringChanged", this._onFilterStringChanged);
        this._model.removeListener("FilterInstalledOnlyChanged", this._onFilterInstalledOnlyChanged);
        this._model.removeListener("ExpandAll", this._onExpandAll);
        this._model.removeListener("CollapseAll", this._onCollapseAll);
        this._model.removeListener("PackageUpdated", this._onPackageUpdated);
        this._availablePackagesList.removeListener("LaunchEditor", this._onLaunchEditor);
        this._importActionBtn.removeEventListener("click", (this._onImport = this._onImport.bind(this)));

        Steve.router.removeListener("RouteDetailsChanged", this._onRouteInfoChanged);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._renderProm) {
            this._renderHeader();
            this._renderContent();
            this._renderFooter();
            return this._renderProm;
        }

        this._rootElem.innerHTML = "";

        this._renderProm = Promise.all([this._renderHeader(), this._renderContent(), this._renderFooter()])
            .then(([headerElem, packageExplorerContentElem, footerElem]) => {
                this._rootElem.appendChild(this._model._userFeedback.renderer.rootElement);
                this._rootElem.appendChild(headerElem);
                this._rootElem.appendChild(packageExplorerContentElem);
                this._rootElem.appendChild(footerElem);

                this._model._distCenterStatus.render();
                this._rootElem.querySelector("game-editor-view-actions-gl left-container").appendChild(this._model._distCenterStatus.rootElement);
            })
            .then(() => {
                this._model._open();
            });

        return this._renderProm;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onShutdown() {
        this._shutdown();
    }
    _onAvailablePackagesReplaced() {
        this.render();
    }
    _onFilterStringChanged() {
        this.render();
        this._availablePackagesList.expandAll();
    }
    _onFilterInstalledOnlyChanged() {
        this.render();
    }
    _onExpandAll() {
        if (this._availablePackagesList) {
            this._availablePackagesList.expandAll();
        }
    }
    _onCollapseAll() {
        if (this._availablePackagesList) {
            this._availablePackagesList.collapseAll();
        }
    }
    _onLaunchEditor(editorState, editorName, opts) {
        this._model.launchEditor(editorState, editorName, opts);
    }

    // prettier-ignore
    _onPackageUpdated(/*newPkgDetails, oldPkgDetails*/) { // jshint ignore:line
        this.render();
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _renderHeader() {
        this._renderFilterInstalledAvailableBtn();
        this._renderImportActionBtn();
        this._renderFilterString();

        if (!this._headerElem) {
            this._headerElem = document.createElement("package-explorer-header-gl");

            this._packageActions = document.createElement("package-actions-gl");
            this._packageActions.appendChild(this._installedAvailableBtn.renderer.rootElement);
            this._packageActions.appendChild(this._importActionBtn);

            this._headerElem.appendChild(this._packageActions);
            this._headerElem.appendChild(this._filterStringControl.renderer.rootElement);
        }
        return Promise.resolve(this._headerElem);
    }

    _renderFooter() {
        if (!this._footerElem) {
            let _footerContents = document.createElement("left-container");
            this._footerElem = document.createElement("game-editor-view-actions-gl");
            this._footerElem.appendChild(_footerContents);
        }
        return Promise.resolve(this._footerElem);
    }

    _renderImportActionBtn() {
        if (!this._importActionBtn) {
            this._importActionBtn = document.createElement("import-action-btn-gl");
            this._importActionBtn.innerHTML = "<span>Import Package</span>";
        }
    }

    _renderFilterString() {
        if (!this._filterStringControl) {
            this._filterStringControl = new InputSearchControl({
                placeholder: "Search Packages",
                mode: "search"
            });
            this._filterStringControl.renderer.rootElement.classList.add("package_filter");
            this._filterStringControl.on("UserValue", () => {
                let filterStr = this._filterStringControl.value;
                this._model.setFilterString(filterStr);
            });
        }
        let filterStr = this._model.filterString;
        this._filterStringControl.value = filterStr;
    }
    _renderFilterInstalledAvailableBtn() {
        if (!this._installedAvailableBtn) {
            //Installed or Available Filter Button Only
            this._installedAvailableBtn = new ButtonGroupControl({
                options: [
                    {
                        label: "Installed",
                        value: "installed"
                    },
                    {
                        label: "Available",
                        value: "available"
                        //TODO go to user preferences for this
                    }
                ],
                value: "available",
                darkTheme: true
            });
            this._installedAvailableBtn.on("UserValue", (val) => {
                switch (val) {
                    case "available":
                        this._model.setFilterInstalledOnly(false);
                        break;
                    case "installed":
                        this._model.setFilterInstalledOnly(true);
                        break;
                }
            });
        }
        //Sync up the button
        if (_.get(Steve.router.getRouteDetails(), "exp", false)) {
            let currentViewName = _.get(Steve.router.getRouteDetails(), "exp", "available");
            this._installedAvailableBtn.setValue(currentViewName);
            this._model.setFilterInstalledOnly(currentViewName === "installed" ? true : false);

            this._resetExpFromRouteDetails();
        } else {
            if (this._model.getFilterInstalledOnly()) {
                this._installedAvailableBtn.setValue("installed");
            } else {
                this._installedAvailableBtn.setValue("available");
            }
        }
    }
    _renderContent() {
        if (!this._packageExplorerContentElem) {
            this._packageExplorerContentElem = document.createElement("package-explorer-content-gl");

            this._packageExplorerContentElem.appendChild(this._availablePackagesList.renderer.rootElement);
        }
        let filteredPackages = this._model.getFilteredPackages();
        this._availablePackagesList.setAvailablePackages(filteredPackages, this._model.getFilterInstalledOnly());
        this._availablePackagesList.expandAll();

        return Promise.resolve(this._packageExplorerContentElem);
    }

    _onImport() {
        return this._model._userFeedback
            .displayCustom(new ImportCapsuleComponent(this._model._logger, this._model._fileFactory), { dontHide: true })
            .then((result) => {
                this._model._userFeedback.displayLoading({
                    message: "Importing your file... This could take a moment"
                });
                //              const showProgFunc = (prog) => {this._model._userFeedback.showLoadingProgress(`${prog.successCount} of ${prog.targetCount}`);};
                switch (result.action) {
                    case "loadCapsule":
                        console.log("Load to capsule");
                        this._loadingPromise = new Promise((resolve, reject) => {
                            let orgId = _.get(this._model._userSession, "activeOrganization.id");
                            this._model._hubbyService.loadCapsule(
                                {
                                    orgId: orgId,
                                    capsuleName: result.capsuleName,
                                    capsuleContent: result.capsuleContent
                                },
                                {
                                    Success: () => {
                                        resolve(true);
                                    },
                                    Failure: (error) => {
                                        reject(error);
                                    }
                                }
                            );
                        });
                        return this._loadingPromise;
                    case "loadRawZipCapsule":
                        console.log("Load raw zip to capsule");
                        this._loadingPromise = new Promise((resolve, reject) => {
                            let orgId = _.get(this._model._userSession, "activeOrganization.id");
                            this._model._hubbyService.loadRawZipCapsule(
                                {
                                    orgId: orgId,
                                    capsuleName: result.capsuleName,
                                    pkgInfo: result.pkgInfo,
                                    base64File: result.base64File
                                },
                                {
                                    Success: () => {
                                        resolve(true);
                                    },
                                    Failure: (error) => {
                                        reject(error);
                                    }
                                }
                            );
                        });
                        return this._loadingPromise;
                }
            })
            .then(() => {
                let userNotificationMessage = "Capsule File Loaded";
                return this._model._userFeedback.displayNotification(userNotificationMessage, {
                    title: "Import Completed"
                });
            })
            .then(() => {
                return this._model._open();
            })
            .catch((err) => {
                if (err.userRejected) {
                    this._model._logger.warn({ error: err }, "_onImport() - User Canceled.");
                } else {
                    this._model._logger.warn({ error: err }, "_onImport() - Error");
                    return this._model._userFeedback.displayError(err).catch(() => {
                        return Promise.resolve();
                    });
                }
            });
    }

    _onRouteInfoChanged() {
        if (event && event.oldURL !== event.newURL) {
            if (_.get(Steve.router.getRouteDetails(), "exp", false)) {
                let currentViewName = _.get(Steve.router.getRouteDetails(), "exp", "available");
                this._installedAvailableBtn.setValue(currentViewName);
                this._model.setFilterInstalledOnly(currentViewName === "installed" ? true : false);
                this._resetExpFromRouteDetails();
            }
        }
    }

    _resetExpFromRouteDetails() {
        let routeDetails = Steve.router._getCurrentRouteDetails();
        delete routeDetails.exp;
        Steve.router.replaceRouteInfo(routeDetails);
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = PackageExplorerLmrbRenderer;
